import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { FiEdit2, FiTrash2, FiPlus, FiCopy } from "react-icons/fi";
import { apiRequest, apiFormRequest, API_BASE_URL } from "../../services/api";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

const getImageUrl = (path) => {
    if (!path) return null;
    return `${API_BASE_URL.replace('/api', '')}/${path.replace(/\\/g, '/')}`;
};

const fetchFeatures = async () => {
    const { data } = await apiRequest("/features");
    return data;
};

const createFeature = async (formData) => {
    const { data } = await apiFormRequest("/features", formData, {
        method: "POST"
    });
    return data;
};

const updateFeature = async ({ id, formData }) => {
    const { data } = await apiFormRequest(`/features/${id}`, formData, {
        method: "PUT"
    });
    return data;
};

const deleteFeature = async (id) => {
    const { data } = await apiRequest(`/features/${id}`, {
        method: "DELETE" // Matches backend route
    });
    return data;
};

const duplicateFeature = async (id) => {
    const { data } = await apiRequest(`/features/${id}/duplicate`, {
        method: "POST"
    });
    return data;
};

const FeaturesManager = () => {
    const queryClient = useQueryClient();
    const [editingId, setEditingId] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [featureToDelete, setFeatureToDelete] = useState(null);

    const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
        defaultValues: {
            active: true
        }
    });

    const { data: features, isLoading, isError } = useQuery({
        queryKey: ["features"],
        queryFn: fetchFeatures,
    });

    const createMutation = useMutation({
        mutationFn: createFeature,
        onSuccess: () => {
            queryClient.invalidateQueries(["features"]);
            toast.success("Feature added successfully!");
            reset();
            setIsAdding(false);
        },
        onError: (err) => toast.error(err.message || "Failed to create feature")
    });

    const updateMutation = useMutation({
        mutationFn: updateFeature,
        onSuccess: () => {
            queryClient.invalidateQueries(["features"]);
            toast.success("Feature updated successfully!");
            setEditingId(null);
            reset();
        },
        onError: (err) => toast.error(err.message || "Failed to update feature")
    });

    const deleteMutation = useMutation({
        mutationFn: deleteFeature,
        onSuccess: () => {
            queryClient.invalidateQueries(["features"]);
            toast.success("Feature deleted successfully!");
            setFeatureToDelete(null);
        },
        onError: (err) => toast.error("Failed to delete feature")
    });

    const duplicateMutation = useMutation({
        mutationFn: duplicateFeature,
        onSuccess: () => {
            queryClient.invalidateQueries(["features"]);
            toast.success("Feature duplicated successfully!");
        },
        onError: (err) => toast.error("Failed to duplicate feature")
    });

    const onSubmit = (data) => {
        const formData = new FormData();
        formData.append("title", data.title);
        formData.append("description", data.description);
        formData.append("icon", data.icon);
        formData.append("active", data.active);

        if (data.image && data.image[0]) {
            formData.append("image", data.image[0]);
        }

        if (editingId) {
            updateMutation.mutate({ id: editingId, formData });
        } else {
            createMutation.mutate(formData);
        }
    };

    const handleEdit = (feature) => {
        setEditingId(feature.id);
        setIsAdding(false);
        setValue("title", feature.title);
        setValue("description", feature.description);
        setValue("icon", feature.icon);
        setValue("active", feature.active === 1 || feature.active === true);
        // Image cannot be set, user must re-upload if they want to change it
    };

    const handleCancel = () => {
        setEditingId(null);
        setIsAdding(false);
        reset();
    };

    const toggleActive = (feature) => {
        const formData = new FormData();
        // Keep existing values, just toggle active
        formData.append("active", !feature.active);
        updateMutation.mutate({ id: feature.id, formData });
    };

    if (isLoading) return <div>Loading features...</div>;
    if (isError) return <div>Error loading features</div>;

    return (
        <div className="mt-8">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-black dark:text-white">Features List</h3>
                {!isAdding && !editingId && (
                    <button
                        onClick={() => {
                            setIsAdding(true);
                            reset({ active: true });
                        }}
                        className="group relative flex items-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:translate-y-0 active:scale-95"
                    >
                        <span className="relative z-10 flex items-center gap-2">
                            <FiPlus className="transition-transform duration-300 group-hover:rotate-90" />
                            Add New Feature
                        </span>
                        <div className="absolute inset-0 z-0 bg-gradient-to-l from-primary to-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    </button>
                )}
            </div>

            {(isAdding || editingId) && (
                <form onSubmit={handleSubmit(onSubmit)} className="mb-8 p-6 bg-gray-50 dark:bg-meta-4/30 rounded-lg border border-stroke dark:border-strokedark">
                    <h4 className="text-md font-semibold mb-4 text-black dark:text-white">{editingId ? "Edit Feature" : "Add New Feature"}</h4>

                    <div className="mb-4 flex items-center gap-2">
                        <label className="font-medium text-black dark:text-white">Active Status:</label>
                        <input
                            type="checkbox"
                            {...register("active")}
                            className="w-5 h-5 text-primary rounded focus:ring-primary dark:bg-gray-700 dark:border-gray-600"
                        />
                        <span className="text-sm text-gray-500">
                            {watch("active") ? "Visible" : "Hidden"}
                        </span>
                    </div>

                    <div className="mb-4">
                        <label className="mb-2 block font-medium text-black dark:text-white">Title</label>
                        <input
                            {...register("title", { required: "Title is required" })}
                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                            placeholder="e.g. Web Development"
                        />
                        {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>}
                    </div>

                    <div className="mb-4">
                        <label className="mb-2 block font-medium text-black dark:text-white">Description</label>
                        <textarea
                            {...register("description", { required: "Description is required" })}
                            rows="3"
                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                            placeholder="Service description..."
                        ></textarea>
                        {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                        <div>
                            <label className="mb-2 block font-medium text-black dark:text-white">SVG Icon Code (Optional)</label>
                            <p className="text-xs text-gray-500 mb-2">Paste the full SVG code here.</p>
                            <textarea
                                {...register("icon")}
                                rows="4"
                                className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 font-family-mono text-sm outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                                placeholder="<svg>...</svg>"
                            ></textarea>
                        </div>
                        <div>
                            <label className="mb-2 block font-medium text-black dark:text-white">Display Image (Optional)</label>
                            <p className="text-xs text-gray-500 mb-2">Upload an image to display instead of/with the icon.</p>
                            <input
                                type="file"
                                accept="image/*"
                                {...register("image")}
                                className="w-full cursor-pointer rounded-lg border-[1.5px] border-stroke bg-transparent font-medium outline-none transition file:mr-5 file:border-collapse file:cursor-pointer file:border-0 file:border-r file:border-solid file:border-stroke file:bg-whiter file:py-3 file:px-5 file:hover:bg-primary file:hover:bg-opacity-10 focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:file:border-form-strokedark dark:file:bg-white/30 dark:file:text-white dark:focus:border-primary"
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={createMutation.isLoading || updateMutation.isLoading}
                            className="group relative flex justify-center overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary px-6 py-2 font-medium text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:translate-y-0 active:scale-95 disabled:opacity-50"
                        >
                            <span className="relative z-10">
                                {createMutation.isLoading || updateMutation.isLoading ? "Saving..." : (editingId ? "Update" : "Save")}
                            </span>
                            <div className="absolute inset-0 z-0 bg-gradient-to-l from-primary to-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                        </button>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="flex justify-center rounded border border-stroke px-6 py-2 font-medium text-black hover:shadow-1 dark:border-strokedark dark:text-white"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature) => (
                    <div key={feature.id} className={`relative p-6 bg-white dark:bg-boxdark rounded-lg border ${feature.active ? 'border-stroke dark:border-strokedark' : 'border-dashed border-gray-300 dark:border-gray-600 opacity-75'} shadow-sm transition-all hover:shadow-md`}>

                        {/* Status Badge */}
                        <div className="absolute top-4 left-4">
                            <button
                                onClick={() => toggleActive(feature)}
                                className={`text-xs px-2 py-1 rounded-full border ${feature.active ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}
                                title="Click to toggle status"
                            >
                                {feature.active ? "Active" : "Hidden"}
                            </button>
                        </div>

                        <div className="absolute top-4 right-4 flex gap-1 bg-white/80 dark:bg-boxdark/80 p-1 rounded-lg backdrop-blur-sm">
                            <button
                                onClick={() => duplicateFeature(feature.id).then(() => queryClient.invalidateQueries(["features"]))}
                                disabled={duplicateMutation.isLoading}
                                className="transform rounded-full p-2 text-blue-500 transition-all duration-300 hover:bg-blue-50 hover:scale-110 hover:shadow-md dark:hover:bg-blue-900/20"
                                title="Duplicate"
                            >
                                <FiCopy size={16} />
                            </button>
                            <button
                                onClick={() => handleEdit(feature)}
                                className="transform rounded-full p-2 text-green-500 transition-all duration-300 hover:bg-green-50 hover:scale-110 hover:shadow-md dark:hover:bg-green-900/20"
                                title="Edit"
                            >
                                <FiEdit2 size={16} />
                            </button>
                            <button
                                onClick={() => setFeatureToDelete(feature)}
                                className="transform rounded-full p-2 text-red-500 transition-all duration-300 hover:bg-red-50 hover:scale-110 hover:shadow-md dark:hover:bg-red-900/20"
                                title="Delete"
                            >
                                <FiTrash2 size={16} />
                            </button>
                        </div>

                        <div className="mt-8 mb-4 h-16 w-16 flex items-center justify-center rounded-lg bg-gray-50 dark:bg-meta-4/30 overflow-hidden">
                            {feature.image ? (
                                <img src={getImageUrl(feature.image)} alt={feature.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-primary w-10 h-10" dangerouslySetInnerHTML={{ __html: feature.icon }} />
                            )}
                        </div>

                        <h4 className="text-xl font-bold mb-2 text-black dark:text-white">{feature.title}</h4>
                        <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3">{feature.description}</p>
                    </div>
                ))}
            </div>

            <DeleteConfirmationModal
                isOpen={!!featureToDelete}
                onClose={() => setFeatureToDelete(null)}
                onConfirm={() => deleteMutation.mutate(featureToDelete.id)}
                title={featureToDelete?.title || "this feature"}
                entityLabel="Feature"
            />
        </div>
    );
};

export default FeaturesManager;
